import { Ref } from 'preact';
import { useAccessibleContext } from '../hooks/UNSAFE_useAccessibleContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { useTextFieldInputHandlers } from '../hooks/UNSAFE_useTextFieldInputHandlers';
import { classNames } from '../utils/UNSAFE_classNames';
import type { TextProps } from '../utils/UNSAFE_interpolations/text';
import { textInterpolations } from '../utils/UNSAFE_interpolations/text';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { ValueUpdateDetail } from '../utils/UNSAFE_valueUpdateDetail';
import { isInputPlaceholderShown } from './TextFieldUtils';
import {
  AriaAttributesSignalExcluded,
  HTMLAttributesSignalExcluded
} from '../utils/UNSAFE_attributeUtils';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TextFieldInputVariantOptions } from './themes/TextFieldInputStyles.css';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import { BundleType } from '../resources/nls/bundle';
import { TextFieldInputRedwoodTheme } from './themes/redwood/TextFieldInputTheme';

const interpolations = [...Object.values(textInterpolations)];

const styleInterpolations = mergeInterpolations<Props>(interpolations);

type Autocomplete = 'off' | 'on' | string;

type OrigPickedPropsFromInput = Pick<
  HTMLAttributesSignalExcluded<HTMLInputElement>,
  'autofocus' | 'id' | 'placeholder' | 'required' | 'role' | 'spellcheck' | 'type'
>;
type RenamedPropsFromInput = Omit<
  OrigPickedPropsFromInput,
  'autofocus' | 'readonly' | 'required'
> & {
  autoFocus?: OrigPickedPropsFromInput['autofocus'];
  isRequired?: OrigPickedPropsFromInput['required'];
};
type InputProps = {
  as?: 'input';
  type?: Pick<HTMLAttributesSignalExcluded<HTMLInputElement>, 'type'>['type'];
  rows?: never;
  inputRef?: Ref<HTMLInputElement>;
  hasPrefix?: boolean;
  hasSuffix?: boolean;
};

type TextAreaProps = {
  as: 'textarea';
  type?: never;
  rows?: number;
  inputRef?: Ref<HTMLTextAreaElement>;
  hasPrefix?: never;
  hasSuffix?: never;
};

type FocusProps = Pick<HTMLAttributesSignalExcluded<HTMLElement>, 'onBlur' | 'onFocus'>;

type UniqueProps = InputProps | TextAreaProps;

type AriaProps = Pick<
  AriaAttributesSignalExcluded,
  | 'aria-autocomplete'
  | 'aria-controls'
  | 'aria-describedby'
  | 'aria-expanded'
  | 'aria-invalid'
  | 'aria-label'
  | 'aria-labelledby'
  | 'aria-valuemax'
  | 'aria-valuemin'
  | 'aria-valuenow'
  | 'aria-valuetext'
>;

type Props = UniqueProps &
  TextProps &
  RenamedPropsFromInput &
  AriaProps &
  FocusProps & {
    autoComplete?: Autocomplete;
    // TODO: Should we expose has* props like these or should we pass this info
    // via FormFieldContext?  (from review on 3/8/22)
    currentCommitValue?: string;
    hasEmptyLabel?: boolean;
    hasEndContent?: boolean;
    hasInsideLabel?: boolean;
    hasStartContent?: boolean;
    value?: string;
    variant?: 'default' | 'embedded';
    onInput?: (detail: ValueUpdateDetail<string>) => void;
    onCommit?: (detail: ValueUpdateDetail<string>) => void;
    onKeyDown?: (event: KeyboardEvent) => void;
    onKeyUp?: (event: KeyboardEvent) => void;
  };

/**
 * Merge multiple ids into a space-separated list
 * @param ids The ids to be merged
 * @returns The space separated string of ids
 */
function mergeIDs(...ids: (string | undefined)[]) {
  return ids.filter(Boolean).join(' ') || undefined;
}

export const TextFieldInput = ({
  as = 'input',
  'aria-autocomplete': ariaAutoComplete,
  'aria-controls': ariaControls,
  'aria-describedby': ariaDescribedBy,
  'aria-expanded': ariaExpanded,
  'aria-invalid': ariaInvalid,
  'aria-label': ariaLabel,
  'aria-labelledby': inputLabelledBy,
  'aria-valuemax': ariaValueMax,
  'aria-valuemin': ariaValueMin,
  'aria-valuenow': ariaValueNow,
  'aria-valuetext': ariaValueText,
  autoComplete,
  autoFocus,
  currentCommitValue,
  hasEmptyLabel,
  hasEndContent = false,
  hasInsideLabel = false,
  hasPrefix = false,
  hasStartContent = false,
  hasSuffix = false,
  id,
  inputRef,
  placeholder,
  isRequired,
  role,
  rows,
  spellcheck,
  type,
  value = '',
  variant = 'default',
  onInput,
  onCommit,
  onKeyDown,
  onKeyUp,
  onBlur,
  onFocus,
  ...props
}: Props) => {
  const { isDisabled, isFocused, isLoading, isReadonly } = useFormFieldContext();
  const { class: styleInterpolationClasses } = styleInterpolations(props);
  const hasValue = value !== '';
  const isTextArea = as === 'textarea';
  const isInput = as === 'input';
  const isPassword = type === 'password';
  const renderPrefix = !isDisabled && hasPrefix;
  const renderSuffix = !isDisabled && hasSuffix;
  const myPlaceholder = isInputPlaceholderShown(hasInsideLabel, hasValue, isFocused)
    ? placeholder
    : undefined;

  const { classes } = useComponentTheme<TextFieldInputVariantOptions>(TextFieldInputRedwoodTheme, {
    type: isPassword ? 'isPassword' : 'notPassword',
    styleVariant: variant,
    textarea: isTextArea ? 'isTextArea' : 'notTextArea',
    input: isInput ? 'isInput' : 'notInput',
    prefix: renderPrefix ? 'hasPrefix' : 'noPrefix',
    suffix: renderSuffix ? 'hasSuffix' : 'noSuffix',
    startContent: hasStartContent ? 'hasStartContent' : 'noStartContent',
    endContent: hasEndContent ? 'hasEndContent' : 'noEndContent',
    insideLabel: hasInsideLabel ? 'hasInsideLabel' : 'noInsideLabel',
    value: hasValue ? 'hasValue' : 'noValue',
    focused: isFocused ? 'isFocused' : 'notFocused',
    disabled: isDisabled ? 'isDisabled' : 'notDisabled'
  });

  const inputClasses = classNames([classes, styleInterpolationClasses]);
  const Comp = as || 'input';
  const textFieldInputHandlers = useTextFieldInputHandlers({
    currentCommitValue,
    value,
    onInput,
    onCommit,
    onKeyDown
  });
  const handlers = mergeProps(textFieldInputHandlers, { onBlur, onFocus });
  // the implicit default for tabindex on an input is 0, so do not explicitly set it.
  // JET-52914 - Implement Tabbable mode API contract in InputText
  // if not tabbable, spread tabbableModeProps on component
  const { isTabbable, tabbableModeProps } = useTabbableMode();
  // TODO: for autoComplete='off', need to configure attrs appropriately to make sure it
  // works across browsers and versions  (from review on 3/11/22)

  // JET-52089: add unsafe API for specifying an external label which is added to ariaLabelledBy on the input,
  // only when labelEdge is 'none' and label is ''. Since ariaLabelledBy takes precedence over all other
  // kinds of labels, this helps to ensure we don't override a meaningful label.
  const { UNSAFE_ariaLabelledBy } = useAccessibleContext();
  const ariaLabelledBy = hasEmptyLabel
    ? mergeIDs(inputLabelledBy, UNSAFE_ariaLabelledBy)
    : inputLabelledBy;

  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const loadingAriaLabel = translations.formControl_loading();

  return (
    <Comp
      aria-autocomplete={ariaAutoComplete}
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      aria-expanded={ariaExpanded}
      aria-invalid={ariaInvalid}
      aria-label={isLoading ? loadingAriaLabel : ariaLabel ? ariaLabel : undefined}
      aria-labelledby={ariaLabelledBy}
      aria-required={isRequired ? true : undefined}
      aria-valuemax={ariaValueMax}
      aria-valuemin={ariaValueMin}
      aria-valuenow={ariaValueNow}
      aria-valuetext={ariaValueText}
      autocomplete={autoComplete}
      autofocus={autoFocus}
      class={inputClasses}
      disabled={isDisabled}
      id={id}
      onKeyUp={onKeyUp}
      placeholder={myPlaceholder}
      readonly={isReadonly}
      // @ts-ignore
      ref={inputRef}
      role={role}
      rows={rows}
      spellcheck={spellcheck}
      type={type}
      value={value}
      {...handlers}
      {...(!isTabbable && tabbableModeProps)}
    />
  );
};
